import { useAuth } from '@clerk/clerk-react';
import { useCallback } from 'react';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string) ?? '/api/v1';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

export function useApi() {
  const { getToken } = useAuth();

  const request = useCallback(
    async <T>(method: string, path: string, body?: unknown): Promise<T> => {
      const token = await getToken();
      const url = path.startsWith('/api/') ? path : `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
      const res = await fetch(url, {
        method,
        headers: {
          'content-type': 'application/json',
          authorization: token ? `Bearer ${token}` : '',
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) {
        let code = 'error';
        let message = res.statusText;
        try {
          const data = (await res.json()) as { error?: { code: string; message: string } };
          if (data.error) {
            code = data.error.code;
            message = data.error.message;
          }
        } catch {
          // ignore
        }
        throw new ApiError(res.status, code, message);
      }
      if (res.status === 204) return undefined as T;
      return (await res.json()) as T;
    },
    [getToken],
  );

  return {
    get:    <T,>(path: string)              => request<T>('GET', path),
    post:   <T,>(path: string, body?: unknown) => request<T>('POST', path, body),
    patch:  <T,>(path: string, body?: unknown) => request<T>('PATCH', path, body),
    put:    <T,>(path: string, body?: unknown) => request<T>('PUT', path, body),
    delete: <T,>(path: string)              => request<T>('DELETE', path),
  };
}
